import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Icon,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  Text,
  Badge,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Select,
} from "@chakra-ui/react";
import {
  AddIcon,
  EditIcon,
  DeleteIcon,
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { useState, useEffect, useContext } from "react";
import MasterDataDrawer from "./MasterDataDrawer";
import { appRequest } from "routes";
import FileViewerModal from "../common/ViewerModal/FileViewerModel";
import { AppContext } from "AppProvider/AppContext";

const FileMasterData = () => {
  const { accountDetails } = useContext(AppContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFile, setSelectedFile] = useState({ file: null, open: false });
  const [editingData, setEditingData] = useState<any>(null);

  const [data, setData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const totalPages = Math.ceil(total / limit);

  // ================= API CALL =================
  const fetchData = async () => {
    try {
      setLoading(true);

      const response: any = await appRequest("documentMaster", "get", {
        page,
        limit,
        search: searchQuery,
      });

      if (response?.status === "success") {
        setData(response.data || []);
        setTotal(response.total || 0);
      } else {
        setData([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Auto fetch when page/limit changes

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchData();
    }, 500);

    return () => clearTimeout(timer);
  }, [page, limit, searchQuery]);

  // ================= Pagination Handlers =================
  const goToNext = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const goToPrev = () => {
    if (page > 1) setPage(page - 1);
  };

  return (
    <Box
      p={4}
      bg="gray.50"
      height="calc(100vh - 80px)"
      overflow="hidden"
      display="flex"
      flexDirection="column"
    >
      {/* Header */}
      <Flex justify="space-between" align="center" mb={2}>
        <Box>
          <Heading size="lg" color="teal.700" mb={1}>
            Master Data Management
          </Heading>
          <Text color="gray.500" fontSize="sm">
            Manage vendor validations and related documents
          </Text>
        </Box>

        {accountDetails?.role === "admin" || accountDetails?.level === "level-1" && (
          <Button leftIcon={<AddIcon />} colorScheme="teal" onClick={onOpen}>
            Add New Master Data
          </Button>
        )}
      </Flex>

      {/* Search */}
      <Flex mb={4} gap={4} bg="white" p={4} borderRadius="lg" boxShadow="sm">
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search by Vendor, Code, Company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="gray.50"
          />
        </InputGroup>

        <Select
          maxW="120px"
          value={limit}
          onChange={(e) => {
            setLimit(Number(e.target.value));
            setPage(1);
          }}
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </Select>
      </Flex>

      {/* Table */}
      <Box flex={1} bg="white" borderRadius="lg" boxShadow="md" overflow="auto">
        {loading ? (
          <Flex justify="center" align="center" h="100%">
            <Spinner size="xl" color="teal.500" />
          </Flex>
        ) : (
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Company</Th>
                <Th>Document Name</Th>
                <Th>Vendor Name</Th>
                <Th>Vendor Code</Th>
                <Th>Valid From</Th>
                <Th>Valid To</Th>
                <Th>Status</Th>
                {accountDetails?.role === "admin" || accountDetails?.level === "level-1" && <Th textAlign="center">Actions</Th>}
              </Tr>
            </Thead>
            <Tbody p={2}>
              {data.length > 0 ? (
                data.map((row) => (
                  <Tr key={row._id} _hover={{ bg: "gray.50" }}>
                    <Td fontWeight="600">{row.companyCode}</Td>
                    <Td
                      cursor="pointer"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setSelectedFile({
                          open: true,
                          file: row?.file,
                        });
                      }}
                    >
                      {row.documentName}
                    </Td>
                    <Td>{row.vendorName}</Td>
                    <Td>
                      <Badge colorScheme="purple">{row.vendorCode}</Badge>
                    </Td>
                    <Td>
                      {row.validateFrom
                        ? new Date(row.validateFrom).toLocaleDateString("en-GB")
                        : "-"}
                    </Td>

                    <Td>
                      {row.validateTo
                        ? new Date(row.validateTo).toLocaleDateString("en-GB")
                        : "-"}
                    </Td>

                    <Td>
                      <Badge colorScheme={row.isActive ? "green" : "red"}>
                        {row.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </Td>
                    {accountDetails?.role === "admin" || accountDetails?.level === "level-1" && <Td textAlign="center">
                      <ButtonGroup size="sm" variant="ghost">
                        <IconButton
                          aria-label="Edit"
                          icon={<EditIcon />}
                          colorScheme="blue"
                          onClick={() => {
                            setEditingData(row);
                            onOpen();
                          }}
                        />
                        <IconButton
                          display="none"
                          aria-label="Delete"
                          icon={<DeleteIcon />}
                          colorScheme="red"
                        />
                      </ButtonGroup>
                    </Td>}
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={7} textAlign="center" py={10}>
                    No Data Found
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Pagination */}
      <Flex
        mt={4}
        justify="space-between"
        align="center"
        bg="white"
        p={4}
        borderRadius="lg"
        boxShadow="sm"
      >
        <Text fontSize="sm" color="gray.600">
          Showing {(page - 1) * limit + 1} - {Math.min(page * limit, total)} of{" "}
          {total}
        </Text>

        <Flex align="center" gap={3}>
          <IconButton
            aria-label="Previous"
            icon={<ChevronLeftIcon />}
            onClick={goToPrev}
            isDisabled={page === 1}
          />

          <Text fontWeight="600">
            Page {page} of {totalPages || 1}
          </Text>

          <IconButton
            aria-label="Next"
            icon={<ChevronRightIcon />}
            onClick={goToNext}
            isDisabled={page === totalPages || totalPages === 0}
          />
        </Flex>
      </Flex>

      <MasterDataDrawer
        getData={() => {
          fetchData();
          setEditingData(null);
        }}
        editData={editingData}
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setEditingData(null);
        }}
      />
      <FileViewerModal
        isOpen={selectedFile.open}
        file={selectedFile.file}
        onClose={() => setSelectedFile({ file: null, open: false })}
      />
    </Box>
  );
};

export default FileMasterData;